import React, { useCallback, useRef, useState } from 'react';
import { UserPlus } from 'lucide-react';
import UserAvatar from './ui/UserAvatar';
import UserProfileCard from './ui/UserProfileCard';
import MemberSelect from './ui/MemberSelect';
import './boards/TaskAssignee.css';

/**
 * The assignee marker on a board card or list row.
 *
 * Three behaviours in one place, because they share an anchor:
 *
 *  - it shows the person's REAL avatar (`UserAvatar`, served by the IdP), the
 *    same picture as in chat, falling back to initials only when the image
 *    cannot load;
 *  - hovering opens the same `UserProfileCard` that clicking an avatar in chat
 *    opens — name and email — so you can tell two colleagues with the same
 *    first name apart without opening the task;
 *  - clicking opens the member picker and assigns directly from the card.
 *
 * The picker IS the trigger: `MemberSelect` renders a `CompactSelectPicker`
 * whose trigger is styled down to nothing but the avatar
 * (`.task-card-assignee-picker`). That is why clicking the avatar opens a menu
 * without a second click, and why the menu is identical to the modal's — it is
 * literally the same component.
 */

// Long enough that dragging the pointer across a board does not strobe cards,
// short enough to feel like a tooltip rather than a delay.
const HOVER_OPEN_MS = 350;
// Grace period while the pointer crosses the gap from avatar to card. The card
// is portalled to <body>, so that journey leaves the avatar's subtree
// immediately and without this the card would close under the cursor.
const HOVER_CLOSE_MS = 120;

export default function TaskAssigneeChip({
  assigneeId,
  member,
  members = [],
  currentUserId = null,
  onAssign,
  size = 28,
}) {
  const [profileRect, setProfileRect] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const anchorRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const interactive = typeof onAssign === 'function';

  const clearTimers = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  };

  const scheduleOpen = useCallback(() => {
    clearTimers();
    // Nothing to show for an unassigned slot, and the profile card must never
    // cover the menu you just opened.
    if (!assigneeId || pickerOpen) return;
    openTimer.current = setTimeout(() => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) setProfileRect(rect);
    }, HOVER_OPEN_MS);
  }, [assigneeId, pickerOpen]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => setProfileRect(null), HOVER_CLOSE_MS);
  }, []);

  const closeNow = useCallback(() => {
    clearTimers();
    setProfileRect(null);
  }, []);

  React.useEffect(() => clearTimers, []);

  // A read-only unassigned slot is nothing at all: an empty circle on every
  // unowned card is noise on a board where most cards are unowned. When you
  // CAN assign, an add-person placeholder appears on card hover instead (see
  // .task-board-card-assignee--empty).
  if (!assigneeId && !interactive) return null;

  const label = member?.name || member?.email || (assigneeId ? 'Unknown member' : 'Unassigned');

  const placeholder = (
    <span className="task-assignee-placeholder" style={{ width: size, height: size }}>
      <UserPlus size={Math.round(size * 0.5)} aria-hidden />
    </span>
  );

  if (!interactive) {
    return (
      <span
        ref={anchorRef}
        className="task-board-card-assignee"
        // No `title`: the browser's own tooltip would appear on top of the
        // profile card, saying less. aria-label stays — screen readers need
        // the name, and they never get the hover card.
        aria-label={`Assigned to ${label}`}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
      >
        {/* `userId` even when `member` is unknown: the avatar is served by id,
            so a person who left the team — or one whose member row has not
            arrived yet — still gets their real picture rather than a "?". */}
        <UserAvatar userId={assigneeId} name={label} size={size} tone="pastel" className="task-assignee-avatar" />
        {profileRect && (
          <UserProfileCard
            userId={assigneeId}
            name={label}
            email={member?.email}
            anchorRect={profileRect}
            onClose={closeNow}
            onMouseEnter={clearTimers}
            onMouseLeave={scheduleClose}
          />
        )}
      </span>
    );
  }

  return (
    <span
      ref={anchorRef}
      className={`task-board-card-assignee${assigneeId ? '' : ' task-board-card-assignee--empty'}`}
      // The card is a drag handle in list view and its parent opens the task
      // detail on click. Both must stop here: dnd-kit's pointer sensor arms on
      // pointerdown, so stopping only the click would still start a drag from
      // the avatar, and the drag would swallow the menu.
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
    >
      <MemberSelect
        className="task-card-assignee-picker"
        value={assigneeId ?? null}
        onChange={onAssign}
        members={members}
        currentUserId={currentUserId}
        avatarSize={size}
        avatarTone="pastel"
        emptyLeading={placeholder}
        aria-label={`Assignee — ${label}`}
        onOpenChange={(open) => {
          setPickerOpen(open);
          if (open) closeNow();
        }}
      />
      {profileRect && !pickerOpen && (
        <UserProfileCard
          userId={assigneeId}
          name={label}
          email={member?.email}
          anchorRect={profileRect}
          onClose={closeNow}
          onMouseEnter={clearTimers}
          onMouseLeave={scheduleClose}
        />
      )}
    </span>
  );
}
